// =============================================================================
// mouvement.js — jetons de mouvement en JS et « mouvement réduit » partagé
// (refonte « Mosaïque vivante », lot L2, 27/09/2026 ; synthèse §5).
//
// MOUVEMENT est le MIROIR des jetons de theme.css (section « Mouvement ») pour
// les styles en ligne et les animations WAAPI (el.animate(…, { easing, duration
// })). Sur le modèle de EASE/DUREES du Vote AG (voteAgShared.js). Toute
// modification se fait DANS LES DEUX fichiers, même commit : le test
// src/mouvement.test.js compare les valeurs.
//
// Trois régimes (amendement de charte du 27/09/2026) :
//   quotidien  120-260 ms, courbes.sortie, aucun dépassement ;
//   spatial    ressorts.doux / courbes.tiroir, aucun rebond visible ;
//   cérémonie  ressorts.ceremonie (rebond ≤ 5 %), RÉSERVÉ aux rituels listés
//              dans src/mouvement.test.js.
//
// Module SANS dépendance (hors React) : le Vote AG l'importe sans tirer le
// graphe d'appShared.js. appShared.js le réexporte pour le reste de l'app.
// =============================================================================
import { useEffect, useState } from "react";

const RESSORT_DOUX = "linear(0, 0.004 1%, 0.033 3%, 0.101 6%, 0.209 9%, 0.342 13%, 0.483 17%, 0.615 22%, 0.728 27%, 0.817 33%, 0.882 39%, 0.927 45%, 0.957 52%, 0.976 59%, 0.987 67%, 0.993 75%, 0.996 83%, 0.998 91%, 1)";
const RESSORT_CEREMONIE = "linear(0, 0.005 1%, 0.041 3%, 0.13 6%, 0.275 9%, 0.457 13%, 0.646 17%, 0.813 22%, 0.937 27%, 1.011 33%, 1.042 39%, 1.044 45%, 1.032 52%, 1.017 59%, 1.006 67%, 1 75%, 0.998 83%, 0.998 91%, 1)";

export const MOUVEMENT = Object.freeze({
  courbes: Object.freeze({
    sortie:      "cubic-bezier(.16,1,.3,1)",     // --ease-out (DS) : entrées, sorties, appui
    sortieDouce: "cubic-bezier(.22,1,.36,1)",    // --ease-out-soft : jauges, fondus longs
    deplacement: "cubic-bezier(.77,0,.175,1)",   // --ease-in-out : déplacement à l'écran
    tiroir:      "cubic-bezier(.32,.72,0,1)",    // --ease-drawer : feuilles et tiroirs
    vague:       "cubic-bezier(.45,.05,.55,.95)", // --ease-wave : vague de profondeur
  }),
  durees: Object.freeze({         // millisecondes
    appui: 120,                   // --dur-press
    rapide: 160,                  // --dur-fast
    popover: 180,                 // --dur-popover
    vue: 200,                     // --dur-view (plafond des View Transitions)
    rouleau: 240,                 // --dur-roll
    modale: 260,                  // --dur-modal
    feuille: 420,                 // --dur-sheet
    feuilleSortie: 240,           // --dur-sheet-out
    ceremonie: 700,               // --dur-ceremony
  }),
  geometrie: Object.freeze({
    montee: 6,                    // --motion-rise (px)
    echelleEntree: 0.96,          // --motion-scale-in
    appui: 0.97,                  // --motion-press
  }),
  ressorts: Object.freeze({
    doux: Object.freeze({ courbe: RESSORT_DOUX, duree: 450, repli: "cubic-bezier(.16,1,.3,1)" }),          // --spring-smooth
    ceremonie: Object.freeze({ courbe: RESSORT_CEREMONIE, duree: 750, repli: "cubic-bezier(.34,1.3,.64,1)" }), // --spring-playful
  }),
});

let linearOk;
/** Courbe d'un ressort utilisable pour une animation WAAPI : le linear()
 *  échantillonné si le navigateur le comprend (Safari 17.2+), sinon le repli. */
export function courbeRessort(nom) {
  const r = MOUVEMENT.ressorts[nom];
  if (!r) throw new Error(`courbeRessort : ressort inconnu « ${nom} »`);
  if (linearOk === undefined) {
    try {
      linearOk = typeof CSS !== "undefined" && typeof CSS.supports === "function"
        && CSS.supports("transition-timing-function", "linear(0, 1)");
    } catch { linearOk = false; }
  }
  return linearOk ? r.courbe : r.repli;
}
/** Tests uniquement : oublie la détection de linear(). */
export function reinitialiserDetectionMouvement() { linearOk = undefined; }

const REQUETE_REDUIT = "(prefers-reduced-motion: reduce)";

/** Mouvement réduit, lecture ponctuelle (hors React : WAAPI, rituels).
 *  Vrai si `?mouvement=reduit` est dans l'URL (recette, écran de salle), si le
 *  réglage <html data-mouvement="reduit"> est posé, ou si le système le demande. */
export function mouvementReduit() {
  if (typeof window === "undefined") return false;
  try { if (new URLSearchParams(window.location.search).get("mouvement") === "reduit") return true; } catch { /* URL illisible */ }
  try { if (document.documentElement.getAttribute("data-mouvement") === "reduit") return true; } catch { /* pas de DOM */ }
  try { return !!(window.matchMedia && window.matchMedia(REQUETE_REDUIT).matches); } catch { return false; }
}

// ── Animer sans risque (lot L5) ─────────────────────────────────────────────
// Un seul point d'appel WAAPI pour les fenêtres, les feuilles, les jauges et
// les chiffres : il rend null là où l'API manque (jsdom des tests, très vieux
// navigateurs), ce qui COURT-CIRCUITE l'animation sans rien changer au
// comportement (la fenêtre se ferme, le chiffre s'écrit). Si le navigateur
// refuse une courbe linear() (Safari < 17.2), l'animation est rejouée avec la
// courbe de sortie : on perd le ressort, jamais le mouvement.

/** Vrai si l'élément sait s'animer (WAAPI). Faux sous jsdom. */
export function peutAnimer(el) {
  return !!el && typeof el.animate === "function";
}

/** el.animate(…) protégé : null si impossible, repli de courbe si refusée. */
export function animer(el, images, options) {
  if (!peutAnimer(el)) return null;
  try { return el.animate(images, options); }
  catch {
    try { return el.animate(images, { ...options, easing: MOUVEMENT.courbes.sortie }); }
    catch { return null; }
  }
}

/** Annule toutes les animations WAAPI en cours sur l'élément (jeton, geste). */
export function annulerAnimations(el) {
  if (el && typeof el.getAnimations === "function") el.getAnimations().forEach((a) => a.cancel());
}

// ── Rien d'animé au clavier (principe n° 4 de la synthèse) ──────────────────
// Dernière modalité de saisie : une touche de navigation ou d'activation
// (Tab, Entrée, Espace, Échap, flèches) ou un raccourci (⌥, ⌘, Ctrl : ⌥1 à
// ⌥9, Alt+←) la passe à « clavier », un appui de pointeur la rend au
// pointeur. Écoute posée une fois, en phase de capture, dès le chargement du
// module (sans effet côté serveur). Suivi UNIQUE du dépôt : la coquille (rail,
// onglets, transitions de vue, lot L6) le consulte aussi.
const TOUCHES_NAVIGATION = /^(Tab|Enter| |Spacebar|Escape|Esc|Arrow\w+|Home|End|PageUp|PageDown)$/;
let derniereSaisie = { clavier: false, t: 0 };
const maintenant = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("keydown", (e) => {
    if (TOUCHES_NAVIGATION.test(e.key || "") || e.altKey || e.metaKey || e.ctrlKey) derniereSaisie = { clavier: true, t: maintenant() };
  }, true);
  document.addEventListener("pointerdown", () => { derniereSaisie = { clavier: false, t: maintenant() }; }, true);
}

/** Vrai si la dernière saisie est une touche de navigation de moins de `ms`. */
export function saisieClavierRecente(ms = 1000) {
  return derniereSaisie.clavier && maintenant() - derniereSaisie.t < ms;
}

/** Mouvement réduit en React, tenu à jour (préférence système, réglage
 *  data-mouvement). Promu depuis voteAgShared.js (ex-useReduitMouvement). */
export function useMouvementReduit() {
  const [reduit, setReduit] = useState(mouvementReduit);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const maj = () => setReduit(mouvementReduit());
    maj();
    let mq = null;
    try { mq = window.matchMedia ? window.matchMedia(REQUETE_REDUIT) : null; } catch { mq = null; }
    if (mq) {
      if (mq.addEventListener) mq.addEventListener("change", maj);
      else if (mq.addListener) mq.addListener(maj);
    }
    let obs = null;
    if (typeof MutationObserver === "function" && typeof document !== "undefined") {
      obs = new MutationObserver(maj);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-mouvement"] });
    }
    return () => {
      if (mq) {
        if (mq.removeEventListener) mq.removeEventListener("change", maj);
        else if (mq.removeListener) mq.removeListener(maj);
      }
      if (obs) obs.disconnect();
    };
  }, []);
  return reduit;
}
